/**
 * ComplianceAuditTemplate (CUR-424 admin-authored file-audit templates).
 *
 * Status / type enums live as native Prisma enums and are exported from
 * `@shared/prisma/enums`. This file carries the JSON-shape contracts for the
 * template tree's `Json` columns. Schemas are the single source of truth —
 * types are inferred, and the parse helpers validate untrusted JSON instead
 * of `as`-casting it.
 */

import { z } from 'zod';

import type { ComplianceAuditPlaybookStep } from '../../constants/compliance-audit';

// Frozen copy of a code-shipped playbook stored on
// `ComplianceAuditTemplateVersion.hardcodedTemplate` for HARDCODED templates —
// exactly the shape the engine consumes today from `COMPLIANCE_AUDIT_PLAYBOOK`.
export type ComplianceAuditHardcodedTemplate = ComplianceAuditPlaybookStep[];

/**
 * Light structural guard for an untrusted `hardcodedTemplate` JSON value: a
 * non-empty array of steps carrying a numeric `order` and a `subSteps` array.
 * Deliberately shallow — the snapshot is written only from the typed shared
 * constant; the guard catches truncated/foreign JSON, not field-level drift.
 */
export function isComplianceAuditHardcodedTemplate(value: unknown): value is ComplianceAuditHardcodedTemplate {
    return (
        Array.isArray(value) &&
        value.length > 0 &&
        value.every(
            (step) =>
                typeof step === 'object' &&
                step !== null &&
                typeof (step as { order?: unknown }).order === 'number' &&
                Array.isArray((step as { subSteps?: unknown }).subSteps),
        )
    );
}

// Worked example stored on `ComplianceAuditTemplateStep.agentExamples` and
// `ComplianceAuditTemplateSubStep.agentExamples` — few-shot anchoring fed to the
// model. For a Check sub-step or a step decision the outcome is
// 'PASS' | 'FAIL' | 'QUERY' | 'NOT_APPLICABLE'; for a CLASSIFICATION sub-step it
// is the value the sub-step lands on ('retail', 'renewal', …) or 'QUERY'. A flag
// is raised in addition to the outcome, never instead of it.

// The fixed outcome vocabularies for worked examples — the single contract
// shared by the builder's outcome dropdowns and the server's example
// validation (matched loosely, case/whitespace/underscore-insensitively). These
// are the prompt-facing terms the model reads and returns; the runtime enums
// spell the resolved states PASSED/FAILED/QUERIED/NOT_APPLICABLE. Step-decision
// and CHECK examples currently share the same four outcomes; they stay
// separate exports because they are distinct contracts that may diverge.
export const COMPLIANCE_AUDIT_STEP_EXAMPLE_OUTCOMES = ['PASS', 'FAIL', 'QUERY', 'NOT_APPLICABLE'] as const;
export const COMPLIANCE_AUDIT_CHECK_EXAMPLE_OUTCOMES = ['PASS', 'FAIL', 'QUERY', 'NOT_APPLICABLE'] as const;
// A CLASSIFICATION example's outcome is one of its effective `produces.options`
// plus this extra: the sub-step may legitimately land on a query.
export const COMPLIANCE_AUDIT_CLASSIFICATION_EXAMPLE_EXTRA_OUTCOMES = ['QUERY'] as const;
export const complianceAuditTemplateCheckExampleSchema = z.object({
    outcome: z.string(),
    shouldRaiseFlag: z.boolean().optional(),
    description: z.string(),
    evidenceBasis: z.string().optional(),
});

export type ComplianceAuditTemplateCheckExample = z.infer<typeof complianceAuditTemplateCheckExampleSchema>;

export const complianceAuditTemplateCheckExamplesSchema = z.array(complianceAuditTemplateCheckExampleSchema);

export type ComplianceAuditTemplateCheckExamples = z.infer<typeof complianceAuditTemplateCheckExamplesSchema>;

// Output of a CLASSIFICATION sub-step, stored on `ComplianceAuditTemplateSubStep.produces`:
// the named value and its options, e.g. { name: 'Transaction type',
// options: ['retail', 'wholesale'] }. `name` is the friendly field name referenced
// verbatim by `appliesWhen` conditions — no separate technical slug exists.
export const complianceAuditTemplateSubStepProducesSchema = z.object({
    name: z.string(),
    options: z.array(z.string()),
});

export type ComplianceAuditTemplateSubStepProduces = z.infer<typeof complianceAuditTemplateSubStepProducesSchema>;

// Structured `appliesWhen` condition stored on `ComplianceAuditTemplateStep.appliesWhen`
// and `ComplianceAuditTemplateSubStep.appliesWhen` (typed Json via
// prisma-json-types-generator — `/// [ComplianceAuditAppliesWhen]`). Exactly one
// clause, discriminated by `operator`; `field` references a Classification's
// `produces.name` verbatim, so it may contain any characters — there is no
// string grammar and no reserved tokens. Multi-clause conditions are
// unrepresentable by design: cross-field conjunction is expressed
// hierarchically (broad gate on the step, narrowing clauses on its sub-steps).
// Negated operators (`!=`, `notIn`) evaluate TRUE when the field never landed
// (permissive negation) — see `evaluateAppliesWhen` in
// `@shared/utils/compliance/compliance-audit-conditions`.

const appliesWhenTextSchema = z.string().trim().min(1).max(2000);

export const complianceAuditAppliesWhenSchema = z.discriminatedUnion('operator', [
    z.object({ operator: z.literal('alwaysApply') }),
    z.object({
        operator: z.enum(['==', '!=']),
        field: appliesWhenTextSchema,
        value: appliesWhenTextSchema,
    }),
    z.object({
        operator: z.enum(['in', 'notIn']),
        field: appliesWhenTextSchema,
        // Capped in line with a Classification's produces options (the values
        // a condition can meaningfully reference).
        values: z.array(appliesWhenTextSchema).min(1).max(100),
    }),
]);

export type ComplianceAuditAppliesWhen = z.infer<typeof complianceAuditAppliesWhenSchema>;

/** The unconditional condition — the default for new rows. */
export const COMPLIANCE_AUDIT_ALWAYS_APPLY = { operator: 'alwaysApply' } as const satisfies ComplianceAuditAppliesWhen;

/**
 * Parse an untrusted `appliesWhen` JSON value. Returns null when the value
 * doesn't match the discriminated union (unknown operator, missing members,
 * empty `values`, a legacy string expression), so call sites render defensively.
 */
export function parseComplianceAuditAppliesWhen(value: unknown): ComplianceAuditAppliesWhen | null {
    const result = complianceAuditAppliesWhenSchema.safeParse(value);
    return result.success ? result.data : null;
}

/**
 * Parse an untrusted `agentExamples` JSON value. Returns null when the value is
 * absent or doesn't match the contract, so call sites render defensively.
 */
export function parseComplianceAuditTemplateCheckExamples(value: unknown): ComplianceAuditTemplateCheckExamples | null {
    const result = complianceAuditTemplateCheckExamplesSchema.safeParse(value);
    return result.success ? result.data : null;
}

/**
 * Parse an untrusted `produces` JSON value. Returns null when the value is
 * absent or doesn't match the contract.
 */
export function parseComplianceAuditTemplateSubStepProduces(
    value: unknown,
): ComplianceAuditTemplateSubStepProduces | null {
    const result = complianceAuditTemplateSubStepProducesSchema.safeParse(value);
    return result.success ? result.data : null;
}
